import React, { useEffect, useRef, useState } from 'react';
import { ValidatedTextField } from '../components/ui/ValidatedTextField';
import { validateEmail, validatePhone, normalizePhone, validateUrl } from '../utils/inputValidators';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  Divider,
  Autocomplete,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Business,
  ArrowBack,
  CheckCircle,
  Search,
  AddBusiness,
  AutoAwesome,
  Logout,
  AttachFile,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ref as storageRef, uploadBytes } from 'firebase/storage';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import { useAuth } from '../contexts/AuthContext';
import { vendorSignupApi } from '../services/vendorSignupApi';
import { storage } from '../firebase';

const steps = ['Choose Brand', 'Vendor Information', 'Review & Submit'];

interface Brand {
  id: string;
  name: string;
  category?: string;
  logoUrl?: string;
  website?: string;
}

interface VendorFormData {
  brandSelectionType: 'claim_existing' | 'register_new';
  selectedBrandId?: string;
  organizationName: string;
  vendorInfo: {
    vendorName: string;
    description?: string;
  };
  contactInfo: {
    contactName: string;
    contactEmail: string;
    contactPhone: string;
    website?: string;
  };
}

export const VendorOrganizationSetupPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Reps arriving from the public vendor sign-up already told us which brand
  // they represent; make them pick it a second time and the funnel leaks.
  const intendedBrand = searchParams.get('brand') || '';
  // An emailed brand rep invitation: the server checks it against this account's verified email.
  const inviteToken = searchParams.get('invite') || '';
  const invitePreview = useQuery({
    queryKey: ['rep-invite-preview', inviteToken],
    queryFn: () => vendorSignupApi.previewRepInvite(inviteToken),
    enabled: Boolean(inviteToken),
    staleTime: 60_000,
  });
  const { logout, currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [dealerLetterFile, setDealerLetterFile] = useState<File | null>(null);
  const [formData, setFormData] = useState<VendorFormData>({
    brandSelectionType: 'claim_existing',
    selectedBrandId: undefined,
    organizationName: '',
    vendorInfo: {
      vendorName: '',
      description: '',
    },
    contactInfo: {
      contactName: '',
      contactEmail: '',
      contactPhone: '',
      website: '',
    },
  });

  // Fetch available brands
  const { data: brandsData, isLoading: brandsLoading } = useQuery({
    queryKey: ['brands'],
    queryFn: async () => {
      const headers = await getAuthHeaders();
      const response = await makeApiCall('/store/brands', {
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to fetch brands');
      }

      return response.json();
    },
  });

  const brands: Brand[] = brandsData?.data || [];

  // Preselect once, after the brand list resolves. If the named brand is not in
  // the catalog the rep is registering a new one, so switch modes rather than
  // leaving them on an empty "claim existing" picker.
  const preselectedRef = useRef(false);
  useEffect(() => {
    if (preselectedRef.current || !intendedBrand || brandsLoading) return;
    preselectedRef.current = true;
    const needle = intendedBrand.trim().toLowerCase();
    const match = brands.find((brand) => brand.name.trim().toLowerCase() === needle);
    if (match) {
      setFormData((prev) => ({ ...prev, brandSelectionType: 'claim_existing' }));
      handleBrandSelect(match);
    } else {
      setFormData((prev) => ({
        ...prev,
        brandSelectionType: 'register_new',
        organizationName: prev.organizationName || intendedBrand,
        vendorInfo: { ...prev.vendorInfo, vendorName: prev.vendorInfo.vendorName || intendedBrand },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandsLoading, brands.length, intendedBrand]);

  const generateDescriptionMutation = useMutation({
    mutationFn: async (brandInfo: { brandName: string; website?: string; category?: string; type?: string }) => {
      const headers = await getAuthHeaders();
      const endpoint = '/onboarding/generate-brand-description';

      const response = await makeApiCall(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(brandInfo),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to generate description';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error?.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return response.json();
    },
    onSuccess: (data) => {
      if (data.data?.description) {
        handleInputChange('vendorInfo.description', data.data.description);
      }
      setIsGeneratingDescription(false);
    },
    onError: (error) => {
      console.error('Error generating description:', error);
      setIsGeneratingDescription(false);
    },
  });

  const createOrganizationMutation = useMutation({
    mutationFn: async (data: VendorFormData) => {
      let dealerLetter: { storagePath: string; fileName: string } | undefined;
      if (dealerLetterFile) {
        if (!currentUser?.uid) {
          throw new Error('You must be signed in to upload a dealer letter');
        }
        const extension = dealerLetterFile.name.split('.').pop()?.toLowerCase() || 'pdf';
        const filePath = `vendor-claim-letters/${currentUser.uid}/${Date.now()}.${extension}`;
        const fileRef = storageRef(storage, filePath);
        await uploadBytes(fileRef, dealerLetterFile, {
          contentType: dealerLetterFile.type || 'application/pdf',
        });
        dealerLetter = {
          storagePath: filePath,
          fileName: dealerLetterFile.name,
        };
      }

      const response = await makeApiCall('/onboarding/complete-vendor', {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          organizationName: data.organizationName,
          selectedBrandId: data.selectedBrandId,
          inviteToken: inviteToken || undefined,
          dealerLetter,
          vendorInfo: {
            vendorName: data.vendorInfo.vendorName,
            description: data.vendorInfo.description,
          },
          contactInfo: {
            contactName: data.contactInfo.contactName,
            contactEmail: data.contactInfo.contactEmail,
            contactPhone: data.contactInfo.contactPhone,
            website: data.contactInfo.website,
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to create vendor organization';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.error?.message || errorMessage;
        } catch {
          errorMessage = errorText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      return response.json();
    },
    onSuccess: (data) => {
      navigate('/app/dashboard', {
        state: {
          dnsToken: data.data?.dnsToken,
          dnsHost: data.data?.dnsHost,
        },
      });
    },
    onError: (error) => {
      console.error('Error creating vendor organization:', error);
    },
  });

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev as any)[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleBrandSelect = (brand: Brand | null) => {
    if (brand) {
      setFormData(prev => ({
        ...prev,
        selectedBrandId: brand.id,
        organizationName: brand.name,
        vendorInfo: {
          ...prev.vendorInfo,
          vendorName: brand.name,
          description: prev.vendorInfo.description || '', // Don't auto-clear existing description
        },
        contactInfo: {
          ...prev.contactInfo,
          website: brand.website || prev.contactInfo.website,
        },
      }));

      // Auto-generate description if description is empty
      if (!formData.vendorInfo.description) {
        handleGenerateDescription(brand.name, brand.website, brand.category);
      }
    }
  };

  const handleGenerateDescription = (brandName: string, website?: string, category?: string) => {
    setIsGeneratingDescription(true);
    generateDescriptionMutation.mutate({
      brandName,
      website,
      category,
    });
  };

  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };

  const handleSubmit = () => {
    createOrganizationMutation.mutate(formData);
  };

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
      setLoggingOut(false);
    }
  };

  const isStepValid = () => {
    if (activeStep === 0) {
      if (formData.brandSelectionType === 'claim_existing') {
        return !!formData.selectedBrandId;
      }
      return true; // Register new brand
    }
    if (activeStep === 1) {
      return (
        formData.organizationName.trim() !== '' &&
        formData.vendorInfo.vendorName.trim() !== '' &&
        formData.contactInfo.contactName.trim() !== '' &&
        formData.contactInfo.contactEmail.trim() !== '' &&
        formData.contactInfo.contactPhone.trim() !== ''
      );
    }
    return true;
  };

  const selectedBrand = brands.find(b => b.id === formData.selectedBrandId);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1.5 }}>
                Choose Your Brand
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
                Select an existing brand to claim, or register a new brand for review
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom sx={{ mb: 2, fontSize: '1rem' }}>
                Brand Selection
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3, mt: 1 }}>
                <Paper
                  onClick={() => handleInputChange('brandSelectionType', 'claim_existing')}
                  sx={{
                    p: 3.5,
                    cursor: 'pointer',
                    border: 2,
                    borderRadius: 3,
                    borderColor: formData.brandSelectionType === 'claim_existing' ? 'primary.main' : 'rgba(0, 0, 0, 0.12)',
                    backgroundColor: formData.brandSelectionType === 'claim_existing' 
                      ? 'rgba(25, 118, 210, 0.04)' 
                      : 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: formData.brandSelectionType === 'claim_existing' 
                        ? 'rgba(25, 118, 210, 0.06)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    boxShadow: formData.brandSelectionType === 'claim_existing' 
                      ? '0 4px 16px rgba(25, 118, 210, 0.15)' 
                      : '0 2px 8px rgba(0, 0, 0, 0.04)',
                  }}
                >
                  {formData.brandSelectionType === 'claim_existing' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 20, color: 'white' }} />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      backgroundColor: formData.brandSelectionType === 'claim_existing' 
                        ? 'primary.main' 
                        : 'rgba(25, 118, 210, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <Business sx={{ 
                        fontSize: 28, 
                        color: formData.brandSelectionType === 'claim_existing' ? 'white' : 'primary.main' 
                      }} />
                    </Box>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.1rem' }}>
                      Claim Existing Brand
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    Select a brand from our catalog that you represent
                  </Typography>
                </Paper>

                <Paper
                  onClick={() => handleInputChange('brandSelectionType', 'register_new')}
                  sx={{
                    p: 3.5,
                    cursor: 'pointer',
                    border: 2,
                    borderRadius: 3,
                    borderColor: formData.brandSelectionType === 'register_new' ? 'primary.main' : 'rgba(0, 0, 0, 0.12)',
                    backgroundColor: formData.brandSelectionType === 'register_new' 
                      ? 'rgba(25, 118, 210, 0.04)' 
                      : 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: formData.brandSelectionType === 'register_new' 
                        ? 'rgba(25, 118, 210, 0.06)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    boxShadow: formData.brandSelectionType === 'register_new' 
                      ? '0 4px 16px rgba(25, 118, 210, 0.15)' 
                      : '0 2px 8px rgba(0, 0, 0, 0.04)',
                  }}
                >
                  {formData.brandSelectionType === 'register_new' && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                      }}
                    >
                      <CheckCircle sx={{ fontSize: 20, color: 'white' }} />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                    <Box sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 2.5,
                      backgroundColor: formData.brandSelectionType === 'register_new' 
                        ? 'primary.main' 
                        : 'rgba(25, 118, 210, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <AddBusiness sx={{ 
                        fontSize: 28, 
                        color: formData.brandSelectionType === 'register_new' ? 'white' : 'primary.main' 
                      }} />
                    </Box>
                    <Typography variant="h6" fontWeight="600" sx={{ fontSize: '1.1rem' }}>
                      Register New Brand
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    Submit a new brand for admin approval
                  </Typography>
                </Paper>
              </Box>
            </Box>

            {formData.brandSelectionType === 'claim_existing' && (
              <Box>
                <Autocomplete
                  options={brands}
                  getOptionLabel={(option) => option.name}
                  value={selectedBrand || null}
                  onChange={(_, value) => handleBrandSelect(value)}
                  loading={brandsLoading}
                  inputValue={searchQuery}
                  onInputChange={(_, newValue) => setSearchQuery(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search and select brand"
                      placeholder="Type to search..."
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2.5,
                          backgroundColor: 'background.paper',
                          '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'primary.main',
                            },
                          },
                          '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderWidth: 2,
                            },
                          },
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: <Search sx={{ mr: 1.5, color: 'text.secondary' }} />,
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box
                      component="li"
                      {...props}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        width: '100%',
                        py: 1.5,
                        px: 2,
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                      }}
                    >
                      {option.logoUrl && (
                        <Box
                          component="img"
                          src={option.logoUrl}
                          alt={option.name}
                          sx={{
                            width: 48,
                            height: 48,
                            objectFit: 'contain',
                            p: 1,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                          }}
                        />
                      )}
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body1" fontWeight="medium" noWrap>
                          {option.name}
                        </Typography>
                        {option.website && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {option.website}
                          </Typography>
                        )}
                      </Box>
                      {option.category && (
                        <Chip
                          label={option.category}
                          size="small"
                          variant="outlined"
                          sx={{ flexShrink: 0 }}
                        />
                      )}
                    </Box>
                  )}
                />
                {selectedBrand && (
                  <Alert 
                    severity="success" 
                    sx={{ 
                      mt: 2,
                      borderRadius: 2.5,
                      backgroundColor: 'rgba(46, 125, 50, 0.08)',
                      border: '1px solid rgba(46, 125, 50, 0.2)',
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      <strong>Selected:</strong> {selectedBrand.name}
                      {selectedBrand.website && ` (${selectedBrand.website})`}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}

            {formData.brandSelectionType === 'register_new' && (
              <Alert 
                severity="info"
                sx={{
                  borderRadius: 2.5,
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  border: '1px solid rgba(25, 118, 210, 0.2)',
                }}
              >
                You'll provide details for your new brand in the next step. Your registration will be submitted for admin approval.
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1.5 }}>
                Vendor Information
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
                Tell us about your vendor organization
              </Typography>
            </Box>

            {formData.brandSelectionType === 'claim_existing' && selectedBrand ? (
              <Paper sx={{ 
                p: 3, 
                bgcolor: 'rgba(25, 118, 210, 0.04)', 
                borderRadius: 3,
                border: '1px solid rgba(25, 118, 210, 0.15)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
              }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                  Selected Brand
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
                  {selectedBrand.logoUrl && (
                    <Box
                      component="img"
                      src={selectedBrand.logoUrl}
                      alt={selectedBrand.name}
                      sx={{
                        width: 64,
                        height: 64,
                        objectFit: 'contain',
                        p: 1.5,
                        backgroundColor: 'white',
                        borderRadius: 2.5,
                        border: '1px solid',
                        borderColor: 'rgba(0, 0, 0, 0.08)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)',
                      }}
                    />
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="600" sx={{ mb: 0.5 }}>
                      {selectedBrand.name}
                    </Typography>
                    {selectedBrand.website && (
                      <Typography variant="body2" color="text.secondary">
                        {selectedBrand.website}
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Organization Name:</strong> {formData.organizationName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Display Name:</strong> {formData.vendorInfo.vendorName}
                  </Typography>
                </Box>
              </Paper>
            ) : (
              <>
                <TextField
                  label="Vendor Organization Name"
                  fullWidth
                  required
                  value={formData.organizationName}
                  onChange={(e) => handleInputChange('organizationName', e.target.value)}
                  placeholder="e.g., Sony Corporation"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                    },
                  }}
                />

                <TextField
                  label="Vendor Display Name"
                  fullWidth
                  required
                  value={formData.vendorInfo.vendorName}
                  onChange={(e) => handleInputChange('vendorInfo.vendorName', e.target.value)}
                  placeholder="e.g., Sony"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2.5,
                    },
                  }}
                />
              </>
            )}

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Description (Optional)
                </Typography>
                <Button
                  size="small"
                  startIcon={isGeneratingDescription ? <CircularProgress size={16} /> : <AutoAwesome />}
                  onClick={() => {
                    const brandName = formData.brandSelectionType === 'claim_existing' && selectedBrand
                      ? selectedBrand.name
                      : formData.vendorInfo.vendorName || formData.organizationName;
                    const website = formData.brandSelectionType === 'claim_existing' && selectedBrand
                      ? selectedBrand.website
                      : formData.contactInfo.website;
                    const category = formData.brandSelectionType === 'claim_existing' && selectedBrand
                      ? selectedBrand.category
                      : undefined;
                    
                    handleGenerateDescription(brandName, website, category);
                  }}
                  disabled={isGeneratingDescription || (!formData.vendorInfo.vendorName && !formData.organizationName)}
                  sx={{ minWidth: 'auto' }}
                >
                  {isGeneratingDescription ? 'Generating...' : 'AI Generate'}
                </Button>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={formData.vendorInfo.description || ''}
                onChange={(e) => handleInputChange('vendorInfo.description', e.target.value)}
                placeholder={
                  isGeneratingDescription
                    ? 'Generating description with AI...'
                    : 'Brief description of your vendor organization. Click "AI Generate" for a suggested description.'
                }
                disabled={isGeneratingDescription}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                  },
                }}
              />
              {formData.vendorInfo.description && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  You can edit or replace this description at any time.
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1.5 }}>
              Contact Information
            </Typography>

            <TextField
              label="Primary Contact Name"
              fullWidth
              required
              value={formData.contactInfo.contactName}
              onChange={(e) => handleInputChange('contactInfo.contactName', e.target.value)}
              placeholder="e.g., John Smith"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                },
              }}
            />

            <ValidatedTextField
              label="Contact Email"
              validate={validateEmail}
              fullWidth
              required
              type="email"
              value={formData.contactInfo.contactEmail}
              onChange={(e) => handleInputChange('contactInfo.contactEmail', e.target.value)}
              placeholder="contact@vendor.com"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                },
              }}
            />

            <ValidatedTextField
              label="Contact Phone"
              validate={validatePhone}
              normalize={normalizePhone}
              onNormalized={(v) => handleInputChange('contactInfo.contactPhone', v)}
              fullWidth
              required
              value={formData.contactInfo.contactPhone}
              onChange={(e) => handleInputChange('contactInfo.contactPhone', e.target.value)}
              placeholder="+1 (555) 123-4567"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2.5,
                },
              }}
            />

            {formData.brandSelectionType === 'claim_existing' && selectedBrand?.website ? (
              <Paper sx={{ 
                p: 2.5, 
                bgcolor: 'rgba(25, 118, 210, 0.04)', 
                borderRadius: 2.5,
                border: '1px solid rgba(25, 118, 210, 0.15)',
              }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
                  Website
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedBrand.website}
                </Typography>
              </Paper>
            ) : (
              <ValidatedTextField
                label="Website (Optional)"
                validate={(v) => validateUrl(v, { allowSchemeless: true })}
                fullWidth
                value={formData.contactInfo.website}
                onChange={(e) => handleInputChange('contactInfo.website', e.target.value)}
                placeholder="https://vendor.com"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                  },
                }}
              />
            )}

            <Box>
              <Typography variant="subtitle1" fontWeight="600" gutterBottom sx={{ mb: 1 }}>
                Dealer / authorisation letter (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Upload a PDF or image if you have a letter confirming you represent this brand. You can also add a DNS TXT record after you submit.
              </Typography>
              <Button
                component="label"
                variant="outlined"
                startIcon={<AttachFile />}
                sx={{ textTransform: 'none', borderRadius: 2.5 }}
              >
                {dealerLetterFile ? dealerLetterFile.name : 'Choose file'}
                <input
                  hidden
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setDealerLetterFile(file);
                  }}
                />
              </Button>
              {dealerLetterFile && (
                <Button
                  size="small"
                  onClick={() => setDealerLetterFile(null)}
                  sx={{ ml: 1, textTransform: 'none' }}
                >
                  Remove
                </Button>
              )}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, mb: 1.5 }}>
                Review & Submit
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.7 }}>
                Review your information before submitting
              </Typography>
            </Box>

            <Paper sx={{ 
              p: 3.5, 
              bgcolor: 'rgba(0, 0, 0, 0.02)', 
              borderRadius: 3,
              border: '1px solid rgba(0, 0, 0, 0.08)',
            }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Brand Selection
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.brandSelectionType === 'claim_existing' && selectedBrand
                  ? `Claiming: ${selectedBrand.name}`
                  : 'Registering new brand (pending approval)'}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Organization Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.organizationName}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Vendor Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.vendorInfo.vendorName}
              </Typography>

              {formData.vendorInfo.description && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Description
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formData.vendorInfo.description}
                  </Typography>
                </>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Contact Name
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.contactInfo.contactName}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Contact Email
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.contactInfo.contactEmail}
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Contact Phone
              </Typography>
              <Typography variant="body1" gutterBottom>
                {formData.contactInfo.contactPhone}
              </Typography>

              {formData.contactInfo.website && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                    Website
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {formData.contactInfo.website}
                  </Typography>
                </>
              )}

              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mt: 2 }}>
                Dealer letter
              </Typography>
              <Typography variant="body1" gutterBottom>
                {dealerLetterFile ? dealerLetterFile.name : 'Not uploaded'}
              </Typography>
            </Paper>

            <Alert 
              severity="info"
              sx={{
                borderRadius: 2.5,
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
                border: '1px solid rgba(25, 118, 210, 0.2)',
              }}
            >
              {formData.brandSelectionType === 'claim_existing'
                ? 'Your vendor application will be submitted for admin approval. You can browse the platform while we review your application.'
                : 'Your new brand registration will be submitted for admin approval. You can browse the platform while we review your application.'}
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: 'background.default',
      pt: { xs: 4, md: 8 },
      pb: { xs: 4, md: 8 },
      px: { xs: 2, sm: 0 },
    }}>
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, gap: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/onboarding/select-type')}
            sx={{ 
              color: 'text.secondary',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Back
          </Button>
          <Button
            variant="text"
            startIcon={<Logout />}
            onClick={handleLogout}
            disabled={loggingOut}
            sx={{ textTransform: 'none', fontWeight: 500 }}
          >
            {loggingOut ? 'Signing out...' : 'Sign out'}
          </Button>
        </Box>

        <Paper sx={{
          p: { xs: 3, sm: 5 },
          borderRadius: 4,
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 5,
            pb: 3,
            borderBottom: '2px solid',
            borderColor: 'divider',
          }}>
            <Box sx={{
              width: 64,
              height: 64,
              borderRadius: 3,
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 3,
              boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
            }}>
              <Business sx={{ fontSize: 32, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" component="h1" sx={{ 
                fontWeight: 700,
                letterSpacing: '-0.02em',
                mb: 0.5,
              }}>
                Vendor Setup
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 400 }}>
                Set up your vendor organization
              </Typography>
            </Box>
          </Box>

          <Stepper 
            activeStep={activeStep} 
            orientation={isMobile ? 'vertical' : 'horizontal'}
            sx={{ 
              mb: { xs: 4, sm: 5 },
              '& .MuiStepLabel-root': {
                '& .MuiStepLabel-label': {
                  fontWeight: 500,
                  fontSize: '0.875rem',
                },
              },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {invitePreview.data?.valid && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You were invited to claim {invitePreview.data.brandName}. Finish setup with the email address the invitation
              was sent to ({invitePreview.data.emailHint}) and your claim will be marked as invited when it is reviewed.
            </Alert>
          )}

          {createOrganizationMutation.isError && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                borderRadius: 2.5,
                backgroundColor: 'rgba(211, 47, 47, 0.08)',
                border: '1px solid rgba(211, 47, 47, 0.2)',
              }}
            >
              {createOrganizationMutation.error instanceof Error
                ? createOrganizationMutation.error.message
                : 'Failed to create vendor organization. Please try again.'}
            </Alert>
          )}

          <Box sx={{ minHeight: 400, mb: 4 }}>
            {renderStepContent()}
          </Box>

          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            pt: 3,
            borderTop: '2px solid',
            borderColor: 'divider',
          }}>
            <Button
              onClick={handleBack}
              disabled={activeStep === 0 || createOrganizationMutation.isPending}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 2.5,
                px: 3,
                py: 1.5,
              }}
            >
              Back
            </Button>

            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={createOrganizationMutation.isPending}
                startIcon={createOrganizationMutation.isPending ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CheckCircle />
                )}
                sx={{ 
                  minWidth: 180,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2.5,
                  px: 4,
                  py: 1.5,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                  },
                }}
              >
                {createOrganizationMutation.isPending ? 'Submitting...' : 'Submit Application'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={!isStepValid()}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2.5,
                  px: 4,
                  py: 1.5,
                  minWidth: 120,
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                  },
                  '&:disabled': {
                    boxShadow: 'none',
                  },
                }}
              >
                Continue
              </Button>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
